import { debounce } from '@mui/material/utils';
import { useCallback, useMemo, useState } from 'react';

import { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import type { Hco, Hcp } from '@/core/graphql/generated/types';

import { useGetHcoAutocompleteLazyQuery } from '../_graphql/query/GetHCOAutocomplete.hook';

const DEBOUNCE_THRESHOLD = 150;
const BATCH_SIZE = 10;
const SEARCH_STRING_MIN_LENGTH = 1;

export type HcoOption = Hco & DropdownItemBase;

type Props = {
	entityCountryId: Hcp['countryId'] | Hco['countryId'];
};

export const useHcoAffiliationsSearch = ({ entityCountryId }: Props) => {
	const [searchHcoAffiliations, { loading, called }] =
		useGetHcoAutocompleteLazyQuery();
	const [options, setOptions] = useState<HcoOption[] | null>(null);

	const updateResults = useCallback(
		async (query: string) => {
			if (!entityCountryId || entityCountryId?.length === 0) return;

			const res = await searchHcoAffiliations({
				variables: {
					payload: {
						searchString: query,
						size: BATCH_SIZE,
						countryId: entityCountryId
					}
				}
			});

			if (
				res.data?.dcrHcoAutocomplete?.__typename === 'HCOAutocompleteResults'
			) {
				setOptions(
					res.data.dcrHcoAutocomplete.results.map(r => ({
						id: r.hco.vid_V,
						name: r.hco.hcoNameCda_V,
						...r.hco
					})) as HcoOption[]
				);
			}
		},
		[entityCountryId, searchHcoAffiliations]
	);

	const resetResults = useCallback(() => {
		setOptions(null);
	}, []);

	const updateSearchDebounced = useMemo(() => {
		return debounce((value: string) => {
			if (!value.trim() || value.trim().length < SEARCH_STRING_MIN_LENGTH) {
				resetResults();
				return;
			}

			if (!entityCountryId || entityCountryId?.length === 0) {
				resetResults();
				return;
			}

			updateResults(value);
		}, DEBOUNCE_THRESHOLD);
	}, [entityCountryId, resetResults, updateResults]);

	const handleChange = (value: string) => {
		updateSearchDebounced(value);
	};

	const handleClear = () => {
		updateSearchDebounced('');
	};

	return {
		handleClear,
		handleChange,
		resetResults,
		options,
		loading,
		called
	};
};
